import * as _ from "lodash";

import { TradingRepo } from "@services/trading.repo";
import { CModelRepo, Locality } from "@services/cmodel.repo";
import {
  AssetClassNames,
  FixedIncomeTypeNames,
} from "@common/enums/AssetClassType";
import { TradeSimulation } from "@models/simulations/tradeSimulation";
import { GenerateSimulationResponse } from "@models/simulations/generateSimulationResponse";
import { extractErrorMessage } from "@common/utils/errorUtils";
import { SecurityDetails, DisplayedValueItem } from "@models/simulations/securityDetails";
import { TradeRecommendation } from "@models/trading/tradeRecommendation";
import { TradeSet } from "@models/trading/tradeSet";
import { PortfolioRepo } from "@services/portfolio.repo";
import { AllocationType } from "@common/enums/AllocationType";
import { AdjustmentType } from "@common/enums/AdjustmentType";
import { AuthService as authService } from "@common/auth.service";

class SimulationController implements angular.IOnInit {
  public portfolioManagers: string[];
  public securities: SecurityDetails[] = [];
  public assetClasses: DisplayedValueItem[] = [
    { displayedName: AssetClassNames.EQUITY, value: AssetClassNames.EQUITY },
    {
      displayedName: "Fixed Income - " + FixedIncomeTypeNames.BOND,
      value: FixedIncomeTypeNames.BOND,
    },
    {
      displayedName: "Fixed Income - " + FixedIncomeTypeNames.FUND,
      value: FixedIncomeTypeNames.FUND,
    },
    {
      displayedName: AssetClassNames.PRECIOUS_METALS,
      value: AssetClassNames.PRECIOUS_METALS,
    },
  ];
  public clientPortfolios: any[];

  public clientSimulations: GenerateSimulationResponse;
  public clients: string[];
  public tradeRecommendations: TradeRecommendation[];
  public cashItems: any[];

  public generating: boolean = false;
  public tradingByPortfolio: boolean = false;
  public securityFilter: string;

  public selectedPortfolio: { portfolioId: number; clientId: number };
  public selectedManager: string;
  public selectedAssetClass: string = AssetClassNames.EQUITY;
  public selectedSecurity: SecurityDetails = <SecurityDetails>{ name: "" };
  public selectedAdjustment: AdjustmentType;
  public percentageAllocationType: AllocationType = AllocationType.ASSET_CLASS;
  public locality: Locality = Locality.Local;
  public percentageAdjustment: number;
  public price: number;

  // For md-select loader to show
  private securityPromise: any;
  private managersPromise: any;

  /** @ngInject */
  constructor(
    private tradingRepo: TradingRepo,
    private portfolioRepo: PortfolioRepo,
    private cModelRepo: CModelRepo,
    private notifyjs: any,
    private authService: authService
  ) {}

  $onInit() {
    this.managersPromise = this.tradingRepo.getTradePortfolioManagers();
    this.securityPromise = this.cModelRepo.getSecurityItemsList(
      AssetClassNames.EQUITY,
      this.locality,
      true
    );

    Promise.all<any[]>([this.managersPromise, this.securityPromise]).then(
      ([managers, securities]) => {
        this.portfolioManagers = managers;
        this.sortAndSetSecurities(securities);
      }
    );
  }

  private sortAndSetSecurities(securities: SecurityDetails[]) {
    this.securities = securities
      .filter((s) => s.currency[0] !== "X")
      .sort((s1, s2) =>
        s1.description.toLowerCase() > s2.description.toLowerCase() ? 1 : -1
      );
  }

  createSimulation() {
    this.executeCreateSimulation();
  }

  private executeCreateSimulation() {
    this.generating = true;
    this.clientSimulations = null;

    let simulationPromise;
    if (this.tradingByPortfolio) {
      simulationPromise = Promise.all([
        this.tradingRepo.getRecommendations(
          this.selectedPortfolio.clientId,
          this.selectedPortfolio.portfolioId
        ),
        this.portfolioRepo.getPortfolioCashItemsForAdmin(
          this.selectedPortfolio.clientId,
          [this.selectedPortfolio.portfolioId],
          null
        ),
      ]).then(([recommendationResp, cashResp]) => {
        var object = recommendationResp.data;
        const tradeSet = new TradeSet(object);
        this.tradeRecommendations = tradeSet.trades.map(
          (t) => new TradeRecommendation(t)
        );

        this.cashItems = cashResp.data as any[];
      });
    } else {
      let generateSimulationObject = {
        portfolioManager: this.selectedManager,
        securityId: this.selectedSecurity.id,
        adjustmentType: this.selectedAdjustment,
        allocationType: this.percentageAllocationType,
        percentageAdjustment: this.percentageAdjustment,
        locality: this.locality,
        price: this.price,
        assetClass: this.selectedAssetClass,
      };
      simulationPromise = this.tradingRepo
        .generateSimulation(generateSimulationObject)
        .then((simulations: GenerateSimulationResponse) => {
          this.clients = Object.keys(simulations);
          this.clientSimulations = _.mapValues(simulations, (s) =>
            s.map((p) => new TradeSimulation(p, this.selectedAssetClass))
          );
        })
        .catch((err) => console.log(err));
    }

    simulationPromise
      .catch((err) => {
        this.notifyjs(extractErrorMessage(err));
      })
      .finally(() => (this.generating = false));
  }

  getManagersClientPortfolios(manager: string) {
    if (!this.tradingByPortfolio) return;

    this.clientPortfolios = [];
    this.tradingRepo
      .getClientPortfolioForManager(manager)
      .then((clientPortfolios) => (this.clientPortfolios = clientPortfolios));
  }

  securityLocalityChange() {
    this.selectedSecurity = <SecurityDetails>{ name: "" };
    this.selectedAssetClass = AssetClassNames.EQUITY;
    if (this.locality + "" == "Local") {
      this.assetClasses = [
        {
          displayedName: AssetClassNames.EQUITY,
          value: AssetClassNames.EQUITY,
        },
        {
          displayedName: "Fixed Income - " + FixedIncomeTypeNames.BOND,
          value: FixedIncomeTypeNames.BOND,
        },
        {
          displayedName: "Fixed Income - " + FixedIncomeTypeNames.FUND,
          value: FixedIncomeTypeNames.FUND,
        },
        {
          displayedName: AssetClassNames.PRECIOUS_METALS,
          value: AssetClassNames.PRECIOUS_METALS,
        },
      ];
    } else {
      this.assetClasses = [
        {
          displayedName: AssetClassNames.EQUITY,
          value: AssetClassNames.EQUITY,
        },
        {
          displayedName: "Fixed Income - " + FixedIncomeTypeNames.BOND,
          value: FixedIncomeTypeNames.BOND,
        },
        {
          displayedName: "Fixed Income - " + FixedIncomeTypeNames.FUND,
          value: FixedIncomeTypeNames.FUND,
        },
        {
          displayedName: AssetClassNames.PRECIOUS_METALS,
          value: AssetClassNames.PRECIOUS_METALS,
        },
        {
          displayedName: AssetClassNames.ALTERNATIVE_INV,
          value: AssetClassNames.ALTERNATIVE_INV,
        },
      ];
    }
    this.securityPromise = this.cModelRepo
      .getSecurityItemsList(AssetClassNames.EQUITY, this.locality, true)
      .then(this.sortAndSetSecurities.bind(this));
  }

  securityClassChange() {
    // if (this.locality === Locality.Local) return;

    this.selectedSecurity = <SecurityDetails>{ name: "" };
    this.securityPromise = this.cModelRepo
      .getSecurityItemsList(this.selectedAssetClass, this.locality, true)
      .then(this.sortAndSetSecurities.bind(this));
  }

  areSecuritiesLoading() {
    return this.securityPromise;
  }

  areManagersLoading() {
    return this.managersPromise;
  }

  determineAdjustmentWording() {
    if (this.selectedAssetClass != FixedIncomeTypeNames.BOND) {
      return true;
    } else {
      return false;
    }
  }
}

export const simulation: angular.IComponentOptions = {
  template: require("./simulations.html"),
  controller: SimulationController,
};



// WEBPACK FOOTER //
// ./src/app/simulations/simulations.ts